import {every, get, isObject, omitBy, snakeCase, some} from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import {isObservableArray, observable} from 'mobx';
import {faInfo} from '@fortawesome/pro-solid-svg-icons';
import {sortNumerically} from '../components/shared/tables/sorters';
import {transformation} from '../utils/transformations';
import {displayableProperty} from './displayableProperty';
import {displayablePropertyParam} from './displayablePropertyParam';
import {renderBreadcrumb} from '../components/shared/HierarchyTreeBreadcrumb';

export class BaseModel {
  id = 0;

  searchableProperties = [];
  saveableProperties = [];
  reducedProperties = [];
  nestedModels = [];

  static faIcon = faInfo;

  static modelDeletedDecoration = {
    deletedAt: observable,
    deletedBy: observable,
  };

  static modelCreateDecoration = {
    updatedAt: observable,
    updatedBy: observable,
  };

  static modelUpdateDecoration = {
    createdAt: observable,
    createdBy: observable,
  };

  static modelChangeDecorations = {
    ...BaseModel.modelCreateDecoration,
    ...BaseModel.modelUpdateDecoration,
    ...BaseModel.modelDeletedDecoration,
  };

  static prepareApiPayload(model) {
    return model;
  }

  /**
   * @description   This properties are used to consolidate properties of a model, which may be
   * extended with additional properties provided by the backend. This properties are used to render tables
   * and detail views in widgets.
   *
   * @type {Array.<DisplayableProperty>}
   */
  displayableProperties = [];
  customPropertyType = null;
  i18nPrefix = '';

  static fromPlainObject(plainObject, {rootStore} = {}) {
    const model = new this();
    Object.assign(model, plainObject);
    if (rootStore) model.populateAttributesFromStore(rootStore);
    return model;
  }

  static search(query, models) {
    if (!query) return models;
    return models.filter((model) => model.fitsQuery(query.toLowerCase()));
  }

  static getPlainSaveableObject(entity) {
    const model = new this();
    const obj = {};
    model.saveableProperties.forEach((key) => {
      obj[key] = entity ? entity[key] : model[key];
    });
    return obj;
  }

  static getSaveableProperties() {
    const model = new this();
    return model.saveableProperties;
  }

  // eslint-disable-next-line no-unused-vars
  static allDisplayableProperties(rootStore, pathPrefix = '', titlePrefix = '') {
    return new this().displayableProperties.map((property) => {
      const clone = {
        ...property,
        key: `${pathPrefix}${property.key}`,
        title: `${titlePrefix}${property.title}`,
      };

      clone.params.forEach((param) => {
        param.path = `${pathPrefix}${param.path}`;
      });

      return clone;
    });
  }

  static displayableCustomProperties(rootStore, type, pathPrefix, titlePrefix) {
    return rootStore.propertyStore.getByType({type}).map((property) => {
      const key = `${pathPrefix}properties.${property.key}`;
      const path = `${pathPrefix}properties["${property.key}"]`;
      const label = property.label ? property.label : property.key;
      return displayableProperty({
        key,
        title: `${titlePrefix}${label}`,
        params: [
          displayablePropertyParam({path, transform: transformation.none}),
        ],
        template: '{value}',
      });
    });
  }

  static getAllDisplayablePropertiesByKeys(store, propertyKeys) {
    const displayableProperties = this.allDisplayableProperties(store);

    const result = [];

    // iterate over property keys to preserve order of the properties
    propertyKeys.forEach((key) => {
      const property = displayableProperties.find((dp) => dp.key === key);
      if (property) {
        result.push(property);
      }
    });

    return result;
  }

  static attributeValuesChanged({a, b, attributes}) {
    return attributes.findIndex((attribute) => a[attribute] !== b[attribute]) >= 0;
  }

  copyAttributesFrom(entity, {preserveReduced = false} = {}) {
    Object.keys(entity).forEach((key) => {
      const isReducedProperty = this.reducedProperties.indexOf(key) >= 0;
      const skipReplacement = isReducedProperty && preserveReduced;
      if (!skipReplacement) {
        if (isObservableArray(this[key]) && isObservableArray(entity[key])) {
          this[key].replace(entity[key]);
        } else {
          this[key] = entity[key];
        }
      }
    });
    return this;
  }

  // eslint-disable-next-line no-unused-vars
  fitsQuery(query, options = {}) {
    return !!this.searchableProperties.find((property) => this.queryFitsProperty(query, property));
  }

  queryFitsProperty(query, property) {
    const value = get(this, property);
    if (!value) {
      return false;
    }

    if (Array.isArray(value) || isObservableArray(value)) {
      if (!(value.length && typeof value[0].fitsQuery === 'function')) {
        return false;
      }
      return some(value, (item) => item.fitsQuery(query));
    }

    if (typeof value === 'object' && typeof value.fitsQuery === 'function') {
      return value.fitsQuery(query);
    }

    return value.toString().toLowerCase().includes(query);
  }

  /**
   * check if a filter or query matches this object.
   * @param filter: object that describes the filter, e.g. { active: true, state: 'new' }
   * @returns {boolean}
   */
  matches(filter = {}) {
    return every(
      Object.keys(filter).map((key) => this.matchesAttributeValue(key, filter[key])),
      Boolean
    );
  }

  matchesAttributeValue(attribute, value) {
    let thisValue = null;

    // eslint-disable-next-line no-prototype-builtins
    if (this.hasOwnProperty(attribute)) {
      thisValue = this[attribute];
      // eslint-disable-next-line no-prototype-builtins
    } else if (this.hasOwnProperty(snakeCase(attribute))) {
      thisValue = this[snakeCase(attribute)];
    } else {
      return true;
    }

    return thisValue === value;
  }

  // eslint-disable-next-line no-unused-vars
  populateAttributesFromStore(rootStore) {
    return this;
  }

  get toFlatObject() {
    // eslint-disable-next-line no-use-before-define
    return toFlatObject(this);
  }

  static findInTree(tree, id) {
    let foundEntity = null;

    if (!(id && tree && tree.length)) {
      return foundEntity;
    }

    // eslint-disable-next-line no-restricted-syntax
    for (const entity of tree) {
      if (entity.id === id) {
        foundEntity = entity;
        break;
      }

      if (entity.children && entity.children.length) {
        const foundEntityFromChildren = this.findInTree(entity.children, id);

        if (foundEntityFromChildren) {
          foundEntity = foundEntityFromChildren;
          break;
        }
      }
    }

    return foundEntity;
  }

  /**
   * Can be used to convert data for https://ant.design/components/tree-select/
   */
  static mapToSelectTreeData(tree, parent = null, isDisabled = () => false) {
    if (!(tree && tree.length)) {
      return [];
    }

    return tree.map((entity) => {
      const path = [entity.name];
      let iParent = parent;
      while (iParent) {
        path.unshift(iParent.title);
        iParent = iParent.parent;
      }

      const treeData = {
        title: entity.name,
        value: entity.id,
        parent,
        path,
        breadcrumb: renderBreadcrumb(path),
        children: [],
        disabled: isDisabled(entity),
      };

      if (entity.children) {
        treeData.children = this.mapToSelectTreeData(entity.children, treeData, isDisabled);
      }

      return treeData;
    });
  }

  static buildTree(models, parentPropName = 'parentId', childrenPropName = 'children') {
    if (!(models && models.length)) {
      return [];
    }

    // cloning to prevent changes on model
    return this.findChildrenByParent(null, models.map(cloneDeep), parentPropName, childrenPropName);
  }

  static findChildrenByParent(parentId, models, parentPropName = 'parentId', childrenPropName = 'children') {
    const result = [];

    // eslint-disable-next-line no-restricted-syntax
    for (const model of models) {
      if (model[parentPropName] === parentId) {
        const children = this.findChildrenByParent(model.id, models, parentPropName, childrenPropName);

        // do not add children prop, if there is no children
        // otherwise a "+" sign will be added to the table
        if (children.length) {
          if (childrenPropName) {
            model[childrenPropName] = children;
          } else {
            result.push(...children);
          }
        }

        result.push(model);
      }
    }

    return result;
  }

  static sortTree(tree, childrenPropName = 'children', sortByPropName = 'sortOrder', sortOrder = 'asc') {
    tree
      .sort((a, b) => sortNumerically(a[sortByPropName], b[sortByPropName]))
      .map((object) => {
        if (object[childrenPropName] && object[childrenPropName].length) {
          this.sortTree(object[childrenPropName], childrenPropName, sortByPropName, sortOrder);
        }

        return object;
      });

    if (sortOrder === 'desc') {
      tree.reverse();
    }
  }

  static searchTree(query, tree) {
    return tree.map((parentNode) => this.searchTreeNode(query, parentNode)).filter((node) => node !== null);
  }

  static searchTreeNode(query, node, parentFits = false) {
    const fitsQuery = node.fitsQuery(query);

    const children = (node.children || []).map((child) => this.searchTreeNode(query, child, fitsQuery || parentFits))
      .filter((child) => child !== null);

    const isVisible = fitsQuery || parentFits || children.length > 0;
    return isVisible ? {
      ...node,
      children: children.length > 0 ? children : undefined,
      __fitsQuery: fitsQuery,
    } : null;
  }

  static buildTreeAndSort(
    models,
    parentProp = 'parentId',
    childrenProp = 'children',
    sortByProp = 'sortOrder',
    sortOrder = 'asc'
  ) {
    const tree = this.buildTree(models, parentProp, childrenProp);
    this.sortTree(tree, childrenProp, sortByProp, sortOrder);

    return tree;
  }
}

export const toFlatObject = (model) => omitBy(model, isObject);
